import { useSelector } from 'react-redux'

// Components
import { Button } from '@tracklib/kit'
import { Input, Select, Form, baseFormValidation } from 'components/form'

// Types
import { Country, State } from 'types'

import styles from './index.css'

type Values = {
  email: string
  password: string
  country: string
  signup_code: string
  ref: string
  alias: string
}

const validate = (values: Values, showSignupCode: boolean, refcodeValid: boolean) => {
  const errors = baseFormValidation({
    required: { fields: ['email', 'password', 'alias', 'country'] },
    length: {
      fields: ['password'],
      options: [9],
      message: 'This password is too short. It must contain at least 9 characters.',
    },
    email: { fields: ['email'] },
    onlyLettersAndNumbers: { fields: ['alias'] },
  })(values)

  const { signup_code, ref, alias } = values
  if (showSignupCode && !signup_code) {
    errors.signup_code = 'Required'
  }

  if (ref && !refcodeValid) {
    errors.ref = 'Invalid refcode'
  }

  if (alias) {
    if (!/^.{0,30}$/i.test(alias)) {
      errors.alias = 'Please limit your username to 30 characters'
    }
  }

  return errors
}

type SignupFormProps = {
  onSubmit: () => Promise<Record<string, unknown>>
  onDone?: () => void
  countryOptions: Array<Country>
  showSignupCode: boolean
  refcodeValid: boolean
  initialValues: Values
}

const SignupForm = ({
  onSubmit,
  onDone,
  countryOptions,
  showSignupCode = false,
  refcodeValid,
  initialValues,
}: SignupFormProps): JSX.Element => {
  const userCountry = useSelector<State, string>(s => s.signup.userCountry)

  const userCountryOption = countryOptions.find(({ id }) => id === userCountry) || (countryOptions.find(({ id }) => id === 'US') as Country)
  const sortedCountryOptions = [
    ...(userCountryOption ? [userCountryOption] : []),
    ...countryOptions.filter(({ id }) => id !== userCountryOption.id),
  ]

  return (
    <Form
      onSubmit={onSubmit}
      onDone={onDone}
      validate={values => validate(values, showSignupCode, refcodeValid)}
      initialValues={{ ...initialValues, country: userCountryOption.id }}
    >
      {({ submitButtonProps, ErrorMessage }) => (
        <>
          <Input label="Email address" name="email" type="email" placeholder="Email" autoComplete="email" />
          <Input label="Password" name="password" type="password" placeholder="Create password" autoComplete="new-password" />
          <Input label="Username" name="alias" type="text" placeholder="Username" autoComplete="username" />
          <Select
            label="Country"
            name="country"
            id="country"
            options={sortedCountryOptions.map(x => ({ id: x.id, label: x.country_name }))}
            placeholder="Select your country"
            autoComplete="country-name"
          />
          <Input label="Signup code" name="signup_code" type="text" placeholder="Signup code" hidden={!showSignupCode} />
          <Input name="initial_referrer" type="text" placeholder="initial referrer" hidden />
          <Input name="landing_url" type="text" placeholder="Landing url" hidden />
          <Input name="signup_url" type="text" placeholder="Signup url" hidden />
          <Input name="ref" type="text" placeholder="referrer" hidden />
          <ErrorMessage />
          <Button className={styles.signupButton} {...submitButtonProps} datatesting="create_account" successText="Done!">
            Create account
          </Button>
          <p className={styles.note}>
            By clicking &#34;Create account&#34; you agree that you have read and accepted the{' '}
            <a href="/terms" target="_blank">
              Terms and Conditions
            </a>
          </p>
        </>
      )}
    </Form>
  )
}

export default SignupForm
